import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import LayersIcon from '@mui/icons-material/Layers';
import ArticleIcon from '@mui/icons-material/Article';
import NotificationsIcon from '@mui/icons-material/Notifications';
import type { ProjectStatus, PublicUser } from '../../types';
import type { AdminProject, ProjectMember } from '../../types/admin';
import { adminProjectApi, usersApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { displayIdentity } from '../../utils/userIdentity';
import { ConfirmDialog } from './ConfirmDialog';
import { ProjectChatSettingsDialog } from './ProjectChatSettingsDialog';
import { PROJECT_STATUS_META, PROJECT_STATUS_OPTIONS } from './statusMeta';

interface ProjectFormState {
  open: boolean;
  mode: 'create' | 'edit';
  project: AdminProject | null;
  name: string;
  description: string;
  status: ProjectStatus;
  customerName: string;
  code: string;
  saving: boolean;
}

const EMPTY_FORM: ProjectFormState = {
  open: false,
  mode: 'create',
  project: null,
  name: '',
  description: '',
  status: 'DRAFT',
  customerName: '',
  code: '',
  saving: false,
};

export function ProjectManagement(): JSX.Element {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<ProjectFormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<AdminProject | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [membersProject, setMembersProject] = useState<AdminProject | null>(null);
  const [chatSettingsProject, setChatSettingsProject] = useState<AdminProject | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [newMemberUserId, setNewMemberUserId] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('');
  const [savingMember, setSavingMember] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [projectPage, userPage] = await Promise.all([
        adminProjectApi.list({ limit: 200 }),
        usersApi.list({ limit: 200 }),
      ]);
      setProjects(projectPage.items);
      setUsers(userPage.items);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải danh sách dự án'), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => setForm({ ...EMPTY_FORM, open: true, mode: 'create' });

  const openEdit = (project: AdminProject) => {
    setForm({
      open: true,
      mode: 'edit',
      project,
      name: project.name,
      description: project.description ?? '',
      status: project.status,
      customerName: project.customer_name ?? '',
      code: project.code ?? '',
      saving: false,
    });
  };

  const closeForm = () => setForm(EMPTY_FORM);

  const handleSubmitForm = async () => {
    if (!form.name.trim()) {
      notify('Vui lòng nhập tên dự án', 'warning');
      return;
    }
    const code = form.code.trim().toUpperCase();
    if (code && !/^[A-Z0-9]{2,6}$/.test(code)) {
      notify('Mã dự án phải 2-6 ký tự in hoa A-Z hoặc số (VD: ABC, QN1)', 'warning');
      return;
    }
    setForm((prev) => ({ ...prev, saving: true }));
    try {
      if (form.mode === 'create') {
        await adminProjectApi.create({
          name: form.name.trim(),
          description: form.description.trim() || null,
          status: form.status,
          customer_name: form.customerName.trim() || null,
          code: code || null,
        });
        notify('Đã tạo dự án mới', 'success');
      } else if (form.project) {
        await adminProjectApi.update(form.project.id, {
          name: form.name.trim(),
          description: form.description.trim() || null,
          status: form.status,
          customer_name: form.customerName.trim() || null,
          code: code || null,
        });
        notify('Đã cập nhật dự án', 'success');
      }
      closeForm();
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu dự án'), 'error');
      setForm((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminProjectApi.remove(deleteTarget.id);
      notify('Đã xóa mềm dự án', 'success');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xóa dự án'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const openMembers = async (project: AdminProject) => {
    setMembersProject(project);
    setNewMemberUserId('');
    setNewMemberRole('');
    setLoadingMembers(true);
    try {
      const fresh = await adminProjectApi.listUsers(project.id);
      setMembers(fresh);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải danh sách thành viên'), 'error');
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const closeMembers = () => {
    setMembersProject(null);
    setMembers([]);
  };

  const handleAddMember = async () => {
    if (!membersProject || !newMemberUserId) return;
    setSavingMember(true);
    try {
      await adminProjectApi.assignUser(membersProject.id, newMemberUserId, newMemberRole.trim() || undefined);
      notify('Đã gán thành viên vào dự án', 'success');
      const fresh = await adminProjectApi.listUsers(membersProject.id);
      setMembers(fresh);
      setNewMemberUserId('');
      setNewMemberRole('');
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể gán thành viên'), 'error');
    } finally {
      setSavingMember(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!membersProject) return;
    try {
      await adminProjectApi.removeUser(membersProject.id, userId);
      notify('Đã gỡ thành viên khỏi dự án', 'success');
      setMembers((prev) => prev.filter((m) => m.id !== userId));
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể gỡ thành viên'), 'error');
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Quản lý dự án
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Thêm dự án
        </Button>
      </Stack>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tên dự án</TableCell>
                <TableCell>Mô tả</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell align="right">Hành động</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && projects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Chưa có dự án nào.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                projects.map((project) => (
                  <TableRow key={project.id} hover>
                    <TableCell>{project.name}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>{project.description || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={PROJECT_STATUS_META[project.status].label}
                        color={PROJECT_STATUS_META[project.status].color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Quản lý giai đoạn / tính năng">
                        <IconButton size="small" onClick={() => navigate(`/admin/projects/${project.id}`)}>
                          <LayersIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Giới thiệu & hướng dẫn sử dụng">
                        <IconButton size="small" onClick={() => navigate(`/admin/projects/${project.id}/intro`)}>
                          <ArticleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Gán thành viên">
                        <IconButton size="small" onClick={() => void openMembers(project)}>
                          <GroupIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Thông báo Google Chat">
                        <IconButton size="small" onClick={() => setChatSettingsProject(project)}>
                          <NotificationsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sửa">
                        <IconButton size="small" onClick={() => openEdit(project)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Xóa mềm">
                        <IconButton size="small" onClick={() => setDeleteTarget(project)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog tạo / sửa dự án */}
      <Dialog open={form.open} onClose={form.saving ? undefined : closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>{form.mode === 'create' ? 'Thêm dự án' : 'Sửa dự án'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Tên dự án"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
              disabled={form.saving}
            />
            <TextField
              label="Mô tả"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              disabled={form.saving}
            />
            <TextField
              label="Tên khách hàng"
              placeholder="VD: Công ty TNHH Quốc Nam"
              helperText='Hiển thị thay cho nhãn chung "Khách phát hiện" ở nhãn nguồn phát hiện bug.'
              value={form.customerName}
              onChange={(e) => setForm((prev) => ({ ...prev, customerName: e.target.value }))}
              fullWidth
              disabled={form.saving}
            />
            <TextField
              label="Mã dự án"
              placeholder="VD: ABC"
              helperText={
                form.mode === 'edit' &&
                form.project &&
                (form.project.next_bug_seq > 1 || form.project.next_feature_seq > 1 || form.project.next_cr_seq > 1)
                  ? 'Dự án đã phát sinh ticket — không thể đổi mã.'
                  : '2-6 ký tự in hoa A-Z hoặc số. Dùng để sinh mã ticket ngắn cho bug/tính năng/yêu cầu thay đổi.'
              }
              value={form.code}
              onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
              fullWidth
              disabled={
                form.saving ||
                (form.mode === 'edit' &&
                  form.project !== null &&
                  !!form.project.code &&
                  (form.project.next_bug_seq > 1 || form.project.next_feature_seq > 1 || form.project.next_cr_seq > 1))
              }
            />
            <FormControl fullWidth disabled={form.saving}>
              <InputLabel id="project-status-label">Trạng thái</InputLabel>
              <Select
                labelId="project-status-label"
                label="Trạng thái"
                value={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value as ProjectStatus }))}
              >
                {PROJECT_STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {PROJECT_STATUS_META[status].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={form.saving}>
            Hủy
          </Button>
          <Button
            onClick={() => void handleSubmitForm()}
            variant="contained"
            disabled={form.saving}
            startIcon={form.saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {form.mode === 'create' ? 'Tạo mới' : 'Lưu thay đổi'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog gán thành viên */}
      <Dialog open={membersProject !== null} onClose={closeMembers} maxWidth="sm" fullWidth>
        <DialogTitle>Thành viên dự án: {membersProject?.name}</DialogTitle>
        <DialogContent dividers>
          {loadingMembers ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <List dense>
                {members.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Chưa có thành viên nào được gán.
                  </Typography>
                )}
                {members.map((member) => (
                  <ListItem
                    key={member.id}
                    secondaryAction={
                      <IconButton edge="end" size="small" onClick={() => void handleRemoveMember(member.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={`${member.name} (${displayIdentity(member)})`}
                      secondary={member.role_in_project || undefined}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel id="add-member-label">Người dùng</InputLabel>
                  <Select
                    labelId="add-member-label"
                    label="Người dùng"
                    value={newMemberUserId}
                    onChange={(e) => setNewMemberUserId(e.target.value)}
                  >
                    {users
                      .filter((u) => !members.some((m) => m.id === u.id))
                      .map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name} ({displayIdentity(u)})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Vai trò trong dự án"
                  size="small"
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                  sx={{ minWidth: 160 }}
                />
                <Button
                  variant="contained"
                  onClick={() => void handleAddMember()}
                  disabled={!newMemberUserId || savingMember}
                >
                  Thêm
                </Button>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMembers}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog cấu hình thông báo Google Chat theo dự án */}
      {chatSettingsProject && (
        <ProjectChatSettingsDialog
          project={chatSettingsProject}
          onClose={() => setChatSettingsProject(null)}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Xóa mềm dự án"
        message={`Dự án "${deleteTarget?.name}" sẽ bị ẩn khỏi hệ thống (xóa mềm). Bạn có chắc chắn muốn tiếp tục?`}
        confirmLabel="Xóa mềm"
        loading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
